import { fetchSettings } from '@requests/profileRequests';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import duration from 'dayjs/plugin/duration';
import between from 'dayjs/plugin/isBetween';
import { User } from 'next-firebase-auth';

dayjs.extend(updateLocale);
dayjs.extend(duration);
dayjs.extend(between);
dayjs.updateLocale('en', {
  weekStart: 1,
});

export type FirstTimeWebUserDiscountPercent = 50;

export type SpecialOfferWindowType =
  | { type: 'None' }
  | {
      type: 'Loading';
    }
  | {
      type: 'FirstTimeWebUserDiscount';
      discountPercent: FirstTimeWebUserDiscountPercent;
      endDate: dayjs.Dayjs;
    };

export function useSpecialOfferWindow(user: User): SpecialOfferWindowType {
  const { data: settings } = useQuery({
    queryKey: ['settings', user.id],
    queryFn: () => fetchSettings(user),
    enabled: user.id !== null,
  });

  if (!settings || !user.id) {
    return { type: 'Loading' };
  }

  const firstSeenInWeb = settings.firstSeenInWeb
    ? dayjs(settings.firstSeenInWeb)
    : undefined;
  const userCreationTime = user.firebaseUser?.metadata.creationTime
    ? dayjs(user.firebaseUser?.metadata.creationTime)
    : undefined;

  // NOTE: new user means creation time is same as first seen in web time
  if (firstSeenInWeb?.isSame(userCreationTime, 'day')) {
    return dayjs().isBetween(
      firstSeenInWeb,
      dayjs(firstSeenInWeb).add(2, 'day'),
      'millisecond',
      '[]',
    )
      ? {
          type: 'FirstTimeWebUserDiscount',
          discountPercent: 50,
          endDate: dayjs(firstSeenInWeb).add(2, 'day'),
        }
      : { type: 'None' };
  } else if (firstSeenInWeb?.isAfter(userCreationTime, 'day')) {
    return dayjs().isBetween(
      firstSeenInWeb,
      dayjs(firstSeenInWeb).add(3, 'day'),
      'millisecond',
      '[]',
    )
      ? {
          type: 'FirstTimeWebUserDiscount',
          discountPercent: 50,
          endDate: dayjs(firstSeenInWeb).add(3, 'day'),
        }
      : { type: 'None' };
  }

  return { type: 'None' };
}
